import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import BOLayout from '@/pages/back-office/BOLayout';
import Datepicker from 'react-tailwindcss-datepicker';
import { startOfToday, format, addDays } from 'date-fns';
import { Chart, registerables } from 'chart.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

Chart.register(...registerables);

function BOStatisticsPage() {
    const [totalOrders, setTotalOrders] = useState(0);
    // Modifier la structure de l'état pour les dates
    const [dateRange, setDateRange] = useState({
        startDate: startOfToday(),
        endDate: addDays(new Date(), 7)
    });

    // Ajouter un état pour les données du graphique
    const [chartData, setChartData] = useState({
        labels: [],
        datasets: []
    });

    // Ajouter un état pour les données du graphique des revenus
    const [revenueChartData, setRevenueChartData] = useState({
        labels: [],
        datasets: []
    });

    const [totalRevenue, setTotalRevenue] = useState(0);
    const [averageBasket, setAverageBasket] = useState(0);

    const ordersChartRef = useRef(null); // Référence pour le graphique des commandes
    const revenueChartRef = useRef(null); // Référence pour le graphique des revenus

    // Ajouter un état pour les données du graphique des commandes par restaurant
    const [ordersByRestaurantChartData, setOrdersByRestaurantChartData] = useState({
        labels: [],
        datasets: []
    });

    // Ajouter un état pour les données du graphique des revenus par restaurant
    const [revenueByRestaurantChartData, setRevenueByRestaurantChartData] = useState({
        labels: [],
        datasets: []
    });

    const [topProducts, setTopProducts] = useState([]);

    const [topProductsFromMenus, setTopProductsFromMenus] = useState([]);

    // Fonction pour générer des couleurs aléatoires
    const generateColors = (numColors) => {
        const colors = [];
        for (let i = 0; i < numColors; i++) {
            const color = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.5)`;
            colors.push(color);
        }
        return colors;
    };

    const fetchData = async () => {
        const { startDate, endDate } = dateRange;
        if (!startDate || !endDate) {
            console.error('Les dates de début ou de fin ne sont pas définies');
            return;
        }

        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/orders-count`, {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            console.log('API Response:', response.data);
            if (response.data && response.data.total !== undefined) {
                setTotalOrders(response.data.total);
            } else {
                console.error('Format de données inattendu:', response.data);
            }

            const responseDaily = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/daily-orders-count`, {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            if (responseDaily.data) {
                const labels = Object.keys(responseDaily.data).map(date =>
                    format(new Date(date), 'dd-MM-yyyy')
                );
                const data = Object.values(responseDaily.data);
                setChartData({
                    labels: labels,
                    datasets: [{
                        label: 'Nombre de commandes par jour',
                        data: data,
                        backgroundColor: 'rgba(54, 162, 235, 0.5)'
                    }]
                });
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des données:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dateRange]);

    useEffect(() => {
        const ctx = document.getElementById('ordersChart').getContext('2d');
        if (ordersChartRef.current) {
            ordersChartRef.current.destroy(); // Détruire le graphique existant
        }
        ordersChartRef.current = new Chart(ctx, {
            type: 'bar',
            data: chartData,
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    title: {
                        display: false,
                    }
                }
            }
        });
    }, [chartData]);

    // Fonction pour récupérer les données des revenus
    const fetchRevenueData = async () => {
        const { startDate, endDate } = dateRange;
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/revenue-by-period`, {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            console.log('Réponse de l\'API pour les revenus:', response.data); // Log pour inspecter la réponse de l'API
            if (response.data && Object.keys(response.data).length > 0) {
                const labels = Object.keys(response.data).map(date =>
                    format(new Date(date), 'dd-MM-yyyy')
                );
                const data = Object.values(response.data);
                setRevenueChartData({
                    labels: labels,
                    datasets: [{
                        label: 'Revenu par jour',
                        data: data,
                        borderColor: 'rgb(75, 192, 192)',
                        backgroundColor: 'rgba(75, 192, 192, 0.5)',
                    }]
                });
            } else {
                console.error('Données de revenu vides ou mal formatées:', response.data);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des données de revenu:', error);
        }
    };

    useEffect(() => {
        fetchRevenueData();
    }, [dateRange]); // Appeler fetchRevenueData lorsque dateRange change

    useEffect(() => {
        const ctx = document.getElementById('revenueChart').getContext('2d');
        if (revenueChartRef.current) {
            revenueChartRef.current.destroy(); // Détruire le graphique existant
        }
        revenueChartRef.current = new Chart(ctx, {
            type: 'line',
            data: revenueChartData,
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    title: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: function (tooltipItem) {
                                return `${tooltipItem.dataset.label}: ${tooltipItem.raw.toFixed(2)} €`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        ticks: {
                            callback: function (value) {
                                return value + ' €';
                            }
                        }
                    }
                }
            }
        });
    }, [revenueChartData]);

    // Assurez-vous que les données sont prêtes avant de tenter de les afficher
    useEffect(() => {
        if (revenueChartData.labels.length > 0 && revenueChartData.datasets.length > 0) {
            console.log('Mise à jour du graphique des revenus avec les données:', revenueChartData);
            const ctx = document.getElementById('revenueChart').getContext('2d');
            if (revenueChartRef.current) {
                revenueChartRef.current.destroy(); // Détruire le graphique existant
            }
            revenueChartRef.current = new Chart(ctx, {
                type: 'line',
                data: revenueChartData,
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        title: {
                            display: false,

                        },
                        tooltip: {
                            callbacks: {
                                label: function (tooltipItem) {
                                    return `${tooltipItem.dataset.label}: ${tooltipItem.raw.toFixed(2)} €`;
                                }
                            }
                        }
                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: function (value) {
                                    return value + ' €';
                                }
                            }
                        }
                    }
                }
            });
        } else {
            console.log('Données du graphique des revenus non prêtes ou incomplètes');
        }
    }, [revenueChartData]);


    const fetchFinancialData = async () => {
        const { startDate, endDate } = dateRange;
        try {
            const responseTotalRevenue = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/total-revenue`, {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            const responseAverageBasket = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/average-basket`, {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            if (responseTotalRevenue.data && responseAverageBasket.data) {
                setTotalRevenue(responseTotalRevenue.data.totalRevenue);
                setAverageBasket(responseAverageBasket.data.averageBasket);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des données financières:', error);
        }
    };

    useEffect(() => {
        fetchFinancialData();
    }, [dateRange]);


    const fetchOrdersByRestaurantData = async () => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/orders-count-by-restaurant`, {
                params: {
                    startDate: dateRange.startDate.toISOString(),
                    endDate: dateRange.endDate.toISOString()
                }
            });
            if (response.data) {
                const labels = Object.keys(response.data.ordersCountByRestaurant);
                const data = Object.values(response.data.ordersCountByRestaurant);
                setOrdersByRestaurantChartData({
                    labels: labels,
                    datasets: [{
                        label: 'Nombre de commandes par restaurant',
                        data: data,
                        backgroundColor: ['rgba(255, 99, 132, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 206, 86, 0.5)', 'rgba(75, 192, 192, 0.5)', 'rgba(153, 102, 255, 0.5)', 'rgba(255, 159, 64, 0.5)']
                    }]
                });
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des données des commandes par restaurant:', error);
        }
    };

    useEffect(() => {
        fetchOrdersByRestaurantData();
    }, [dateRange]); // Appeler fetchOrdersByRestaurantData lorsque dateRange change

    // Initialisation du graphique
    useEffect(() => {
        const canvas = document.getElementById('ordersByRestaurantChart');
        if (canvas && ordersByRestaurantChartData.labels.length > 0) {
            const ctx = canvas.getContext('2d');
            const ordersByRestaurantChart = new Chart(ctx, {
                type: 'pie',
                data: ordersByRestaurantChartData,
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        title: {
                            display: true,
                            text: 'Nombre de commandes par restaurant'
                        }
                    }
                }
            });

            return () => {
                ordersByRestaurantChart.destroy();
            };
        }
    }, [ordersByRestaurantChartData]);

    // Fonction pour récupérer les données des revenus par restaurant
    const fetchRevenueByRestaurantData = async () => {
        const { startDate, endDate } = dateRange;
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/revenue-by-restaurant`, {
                params: {
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString()
                }
            });
            if (response.data) {
                const labels = Object.keys(response.data.revenueByRestaurant);
                const data = Object.values(response.data.revenueByRestaurant).map(value => parseFloat(value)); // Assurez-vous que les valeurs sont des nombres
                const colors = generateColors(labels.length); // Générer des couleurs pour chaque restaurant
                setRevenueByRestaurantChartData({
                    labels: labels, // Utiliser les noms des restaurants comme labels
                    datasets: [{
                        label: 'Chiffre d\'affaires par restaurant',
                        data: data,
                        backgroundColor: colors // Appliquer les couleurs générées
                    }]
                });
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des données de chiffre d\'affaires par restaurant:', error);
        }
    };

    useEffect(() => {
        fetchRevenueByRestaurantData();
    }, [dateRange]);

    // Initialisation du graphique des revenus par restaurant
    useEffect(() => {
        const canvas = document.getElementById('revenueByRestaurantChart');
        if (canvas && revenueByRestaurantChartData.labels.length > 0) {
            const ctx = canvas.getContext('2d');
            const revenueByRestaurantChart = new Chart(ctx, {
                type: 'bar',
                data: revenueByRestaurantChartData,
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'top',
                        },
                        title: {
                            display: true,
                            text: 'Chiffre d\'affaires par restaurant'
                        }
                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: function (value) {
                                    return value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
                                }
                            }
                        }
                    }
                }
            });

            return () => {
                revenueByRestaurantChart.destroy();
            };
        }
    }, [revenueByRestaurantChartData]);

    const fetchTopProducts = async () => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/top-products`);
            if (response.data) {
                setTopProducts(response.data);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des produits les mieux vendus:', error);
        }
    };

    useEffect(() => {
        fetchTopProducts();
    }, []);

    const fetchTopProductsFromMenus = async () => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/statistics/top-products-from-menus`);
            if (response.data) {
                setTopProductsFromMenus(response.data);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des produits les mieux vendus des menus:', error);
        }
    };

    useEffect(() => {
        fetchTopProductsFromMenus();
    }, []);

    const exportToPDF = () => {
        const input = document.getElementById('statistics-content');
        html2canvas(input)
            .then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');
                const imgProps = pdf.getImageProperties(imgData);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                pdf.save('statistiques.pdf');
            });
    };

    return (
        <BOLayout>
            <div className="p-4" id="statistics-content">
                <div className="flex justify-end mb-4">
                    <button
                        onClick={exportToPDF}
                        className="bg-primary text-white font-bold py-2 px-4 rounded"
                    >
                        Exporter en PDF
                    </button>
                </div>
                <h2 className="text-2xl font-bold mb-4 text-center">Statistiques des commandes</h2>
                <div className="flex gap-4 mb-4 justify-center">
                    <Datepicker
                        i18n="fr"
                        displayFormat="DD/MM/YYYY"
                        primaryColor={"amber"}
                        showFooter={true}
                        showShortcuts={true}
                        configs={{
                            shortcuts: {
                                today: "Aujourd'hui",
                                yesterday: "Hier",
                                past: period => `Les ${period} derniers jours`,
                                currentMonth: "Ce mois-ci",
                                pastMonth: "Le mois dernier"
                            },
                            footer: {
                                cancel: "Quitter",
                                apply: "Appliquer"
                            }
                        }}
                        value={dateRange}
                        onChange={newValue => setDateRange(newValue)}
                        className="datepicker-light"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
                    <div className="bg-primary shadow rounded-lg p-4 flex flex-col items-center justify-center">
                        <h2 className="text-xl text-white font-bold">Nombre total de commandes</h2>
                        <p className="text-2xl text-white ">{totalOrders}</p>
                    </div>

                    <div className="bg-primary shadow rounded-lg p-4 flex flex-col items-center justify-center">
                        <h2 className="text-xl text-white font-bold">Chiffre d'affaires total</h2>
                        <p className="text-2xl text-white ">{totalRevenue ? totalRevenue.toFixed(2) : '0.00'} €</p>
                    </div>

                    <div className="bg-primary shadow rounded-lg p-4 flex flex-col items-center justify-center">
                        <h2 className="text-xl text-white font-bold">Panier moyen</h2>
                        <p className="text-2xl text-white ">{averageBasket ? averageBasket.toFixed(2) : '0.00'} €</p>
                    </div>
                </div>

                <div className="flex">
                    <div className="w-1/2 h-64">
                        <canvas id="ordersChart" className="w-1/2 h-64"></canvas>
                    </div>
                    <div className="w-1/2 h-64">
                        <canvas id="revenueChart" className="w-1/2 h-64"></canvas>
                    </div>
                </div>

                <div className="flex">
                    <div className="w-1/2 h-64">
                        <canvas id="ordersByRestaurantChart" className="w-full h-full"></canvas>
                    </div>
                    <div className="w-1/2 h-64">
                        <canvas id="revenueByRestaurantChart" className="w-full h-full"></canvas>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                    <div>
                        <h3 className="text-xl font-bold mb-4">Formules les Mieux Vendus</h3>
                        <ul className="list-disc pl-5">
                            {topProducts.map((product, index) => (
                                <li key={index} className="mb-2">
                                    <span className="font-semibold">{product.name}</span> - {product.unitsSold} unités vendues, {product.revenue.toFixed(2)} €
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h3 className="text-xl font-bold mb-4">Top 10 Produits les Mieux Vendus</h3>
                        <ul className="list-disc pl-5">
                            {topProductsFromMenus.map((product, index) => (
                                <li key={index} className="mb-2">
                                    <span className="font-semibold">{product.name}</span> - {product.unitsSold} unités vendues, {product.revenue.toFixed(2)} €
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </BOLayout>
    );
}

export default BOStatisticsPage;
